import React, { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CloudSun } from 'lucide-react';
import { pageCache } from '../utils/pageCache';
import {
  PROBLEM_WORDS, WeatherPayload, dayLabel, describeLocation, fixtureSource, forecastDownDays, readProblems, salesStale,
  splitConditions,
} from '../utils/weather';
import LoadingScreen from '../components/LoadingScreen';
import ActionsCard from '../components/weather/ActionsCard';
import { ClaudeNote, GuessesCard } from '../components/weather/ClaudeWords';
import PatternCard from '../components/weather/PatternCard';
import ProgressRow from '../components/weather/ProgressRow';
import RecordCard from '../components/weather/RecordCard';
import TomorrowCard from '../components/weather/TomorrowCard';
import WeekStrip from '../components/weather/WeekStrip';
import YearHeatmap from '../components/weather/YearHeatmap';
import { NOTE } from '../components/weather/weatherUi';

// Sales › Weather, laid out in the order an owner asks: what tomorrow does to
// me, the week, what weather does here (with the proof), have you been right,
// the year, what to do about it, and Claude's words last. Every number was
// computed by the backend's code (engine/weather/); Claude writes only words.
// A part whose field is missing from an older payload is simply not drawn.

const URL = '/api/weather';
const CARD = 'rounded-[14px] border border-[#E6E2DE] bg-white p-6 shadow-card';
const TITLE = 'font-serif text-lg text-[#2D2A2E]';

function Card({ id, title, note, children }: { id: string; title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className={CARD}>
      <h2 id={id} className={TITLE}>{title}</h2>
      {note && <p className={`${NOTE} mt-0.5`}>{note}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** The loaded page's shape in hairline blocks, so nothing jumps when the numbers land. */
function Skeleton() {
  const bar = (w: string) => <div className={`h-3 rounded-full bg-[#E6E2DE]/70 ${w}`} />;
  return (
    <div aria-busy="true" className="space-y-6">
      <section data-skeleton className={CARD}>
        <h2 className={TITLE}>Tomorrow</h2>
        <p className="mt-4 font-sans text-sm text-[#2D2A2E]">Matching your sales to the weather. Check back in a minute.</p>
        <div className="mt-4 space-y-2">{bar('w-2/5')}{bar('w-3/5')}</div>
      </section>
      <section data-skeleton className={CARD}>
        <div className="h-4 w-32 rounded-full bg-[#E6E2DE]/70" />
        <div className="mt-5 grid gap-2 md:grid-cols-7">
          {Array.from({ length: 7 }, (_, i) => <div key={i} className="h-12 rounded-[10px] bg-[#FAF9F7] md:h-32" />)}
        </div>
      </section>
      <section data-skeleton className={CARD}>
        <div className="h-4 w-40 rounded-full bg-[#E6E2DE]/70" />
        <div className="mt-5 grid gap-6 md:grid-cols-[12rem_minmax(0,1fr)]">
          <div className="space-y-2">{bar('w-3/4')}{bar('w-1/2')}{bar('w-2/3')}</div>
          <div className="h-36 rounded-[10px] bg-[#FAF9F7]" />
        </div>
      </section>
    </div>
  );
}

function Header({ data }: { data: WeatherPayload | null }) {
  const ready = data?.status === 'ready';
  const stale = ready && salesStale(data.data_through);
  return (
    <div className="mb-6">
      <h1 className="font-serif text-2xl text-[#2D2A2E]">Weather</h1>
      {ready && (
        <div className="mt-1.5 font-sans text-xs leading-[1.6] text-[#4A2D5E]">
          <p>
            {data.location.label}
            {data.location.sample ? ' · the sample café' : ''}
          </p>
          {data.location.sample && <p>Modeled sales on real weather</p>}
          <p className="tabular-nums">
            {stale ? (
              <span data-stale className="font-medium text-[#B54A4A]">sales through {dayLabel(data.data_through)}</span>
            ) : (
              <span>sales through {dayLabel(data.data_through)}</span>
            )}
            {' '}· updated {dayLabel(data.computed_on)}
          </p>
        </div>
      )}
    </div>
  );
}

function Ready({ data }: { data: WeatherPayload }) {
  const { forecast, notices } = readProblems(data.problems);
  // The backend still sends seven days when the forecast fails; an empty list is drawn as seven empty days.
  const down = forecast;
  const outlook = (data.outlook || []).length ? data.outlook : forecast ? forecastDownDays(data.computed_on) : [];
  const { cards, progress } = splitConditions(data.patterns || [], data.not_yet || []);
  const quiet = notices.filter((n) => n !== PROBLEM_WORDS.forecast);
  return (
    <div className="space-y-6">
      {quiet.length > 0 && (
        <ul className="space-y-2">
          {quiet.map((n) => (
            <li key={n} data-notice className="rounded-[10px] border border-[#E6E2DE] bg-white px-4 py-2.5 font-sans text-xs leading-[1.6] text-[#6B6670]">{n}</li>
          ))}
        </ul>
      )}

      <TomorrowCard days={outlook} down={down} />

      {outlook.length > 0 && (
        <Card id="weather-week" title="The week ahead" note={forecast ? 'No forecast this morning. The week fills in when it comes back.' : undefined}>
          <WeekStrip days={outlook} />
        </Card>
      )}

      <section aria-labelledby="weather-patterns" className="pt-2">
        <h2 id="weather-patterns" className="font-serif text-xl text-[#2D2A2E]">What weather does here</h2>
        <p className="mt-1 max-w-[65ch] font-sans text-sm leading-[1.6] text-[#6B6670]">
          {cards.length
            ? 'Each weather against your ordinary days, from your own register. A pattern shows only once it has held up on days it was not built from.'
            : progress.length
              ? 'No weather pattern has held up in your sales yet. Here is how close each one is.'
              : 'No weather pattern has held up in your sales yet.'}
        </p>
        <div className="mt-4 space-y-4">
          {cards.map((c) => <PatternCard key={c.condition} card={c} />)}
          {progress.length > 0 && (
            <div className={CARD}>
              <h3 className="font-serif text-base text-[#2D2A2E]">{cards.length ? 'Still learning' : 'How close each one is'}</h3>
              <div className="mt-1 divide-y divide-[#E6E2DE]">
                {progress.map((g) => <ProgressRow key={g.condition} gate={g} />)}
              </div>
            </div>
          )}
        </div>
      </section>

      {data.record && <RecordCard record={data.record} dataThrough={data.data_through} />}

      {data.year && data.year.length > 0 && (
        <Card id="weather-year" title="The year at this shop" note="Every open day of the last 52 weeks against its usual. It fills in the longer you stay.">
          <YearHeatmap year={data.year} />
        </Card>
      )}

      {data.actions && <ActionsCard actions={data.actions} />}
      <ClaudeNote note={data.note} />
      <GuessesCard guesses={data.guesses || []} />
    </div>
  );
}

export default function WeatherPage() {
  // Dev only: ?fixture=1 (the bundled sample) or ?fixture=<file> in public/.
  // The NODE_ENV check is literal so a production build drops this path.
  const fixture = process.env.NODE_ENV !== 'production'
    ? fixtureSource(window.location.search, process.env.NODE_ENV, window.location.pathname)
    : null;
  const [data, setData] = useState<any>(() => (fixture ? null : pageCache.get(URL) ?? null));
  const [loading, setLoading] = useState(() => (fixture ? true : !pageCache.has(URL)));
  const navigate = useNavigate();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && fixture) {
      const load: Promise<WeatherPayload> = fixture === 'bundled'
        ? import('../utils/weatherFixture').then((mod) => mod.WEATHER_FIXTURE)
        : fetch(fixture).then((res) => res.json());
      load.then(setData).catch(() => setData(null)).finally(() => setLoading(false));
      return;
    }
    axios.get(URL)
      .then((res) => { pageCache.set(URL, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(URL)) setData(null); })
      .finally(() => setLoading(false));
  }, [fixture]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <Header data={data} />
      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : data?.status === 'no_location' || !data ? (
        <div className="flex flex-col items-center justify-center py-24">
          <CloudSun size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">Where is your shop?</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">
            {describeLocation(data) || 'Add your street address and ClearPath learns how rain, snow, heat and cold move your sales.'}
          </p>
          <button onClick={() => navigate('/settings/account')} className="bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E] focus-visible:ring-offset-2">
            Add address
          </button>
        </div>
      ) : data.status === 'no_sales' ? (
        <p className="font-sans text-sm text-[#6B6670] py-12 text-center">Weather needs register sales to learn from.</p>
      ) : data.status !== 'ready' ? (
        <Skeleton />
      ) : (
        <Ready data={data} />
      )}
    </motion.div>
  );
}
